import { ArrowLeft, Loader2, Trash2 } from "lucide-react"
import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { MemoryTab } from "@/components/memory-tab"
import { PluginsTab } from "@/components/plugins-tab"
import { TelegramCard } from "@/components/telegram-card"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useT } from "@/i18n/provider"
import { ApiError, api, toastApiError } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import type { Connection, McpTool, Provider, ResponseStyle } from "@/lib/types"

interface Selection {
  connectionId: string
  /** "*" means every tool this server exposes, including ones added later. */
  toolNames: Set<string> | "all"
}

export function AgentEditorPage() {
  const t = useT()
  const { agentId } = useParams()
  const [searchParams] = useSearchParams()
  const requestedTab = searchParams.get("tab") ?? ""
  const initialTab = ["settings", "plugins", "telegram", "memory"].includes(requestedTab)
    ? requestedTab
    : "settings"
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.is_admin ?? false
  const isNew = !agentId

  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [providers, setProviders] = React.useState<Provider[]>([])
  const [connections, setConnections] = React.useState<Connection[]>([])
  const [toolsByConnection, setToolsByConnection] = React.useState<Record<string, McpTool[]>>({})

  const [name, setName] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [providerKey, setProviderKey] = React.useState("")
  const [model, setModel] = React.useState("")
  const [systemPrompt, setSystemPrompt] = React.useState("")
  const [temperature, setTemperature] = React.useState("")
  const [maxIterations, setMaxIterations] = React.useState("8")
  const [isActive, setIsActive] = React.useState(true)
  const [webToolsEnabled, setWebToolsEnabled] = React.useState(true)
  const [responseStyle, setResponseStyle] = React.useState<ResponseStyle>("concise")
  const [selections, setSelections] = React.useState<Record<string, Selection>>({})

  React.useEffect(() => {
    async function load() {
      try {
        const [providerList, connectionList] = await Promise.all([
          api.providers(),
          api.listConnections(),
        ])
        setProviders(providerList)
        setConnections(connectionList)

        const tools: Record<string, McpTool[]> = {}
        await Promise.all(
          connectionList.map(async (connection) => {
            tools[connection.id] = await api.connectionTools(connection.id)
          }),
        )
        setToolsByConnection(tools)

        if (agentId) {
          const agent = await api.getAgent(agentId)
          setName(agent.name)
          setDescription(agent.description ?? "")
          setProviderKey(agent.provider_key)
          setModel(agent.model)
          setSystemPrompt(agent.system_prompt ?? "")
          setTemperature(agent.temperature?.toString() ?? "")
          setMaxIterations(agent.max_iterations.toString())
          setIsActive(agent.is_active)
          setWebToolsEnabled(agent.web_tools_enabled)
          setResponseStyle(agent.response_style)

          const next: Record<string, Selection> = {}
          for (const binding of agent.tools) {
            const existing = next[binding.connection_id]
            if (binding.tool_name === "*") {
              next[binding.connection_id] = {
                connectionId: binding.connection_id,
                toolNames: "all",
              }
            } else if (!existing || existing.toolNames !== "all") {
              const set =
                existing && existing.toolNames !== "all"
                  ? existing.toolNames
                  : new Set<string>()
              set.add(binding.tool_name)
              next[binding.connection_id] = {
                connectionId: binding.connection_id,
                toolNames: set,
              }
            }
          }
          setSelections(next)
        } else {
          const ready = providerList.find((provider) => provider.ready) ?? providerList[0]
          if (ready) {
            setProviderKey(ready.provider_key)
            setModel(ready.default_model ?? "")
          }
        }
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : t("editor.toasts.loadFailed"))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [agentId, t])

  const provider = providers.find((entry) => entry.provider_key === providerKey)

  function toggleConnection(connection: Connection, enabled: boolean) {
    setSelections((current) => {
      const next = { ...current }
      if (enabled) next[connection.id] = { connectionId: connection.id, toolNames: "all" }
      else delete next[connection.id]
      return next
    })
  }

  function toggleTool(connection: Connection, toolName: string) {
    setSelections((current) => {
      const next = { ...current }
      const entry = next[connection.id]
      const all = (toolsByConnection[connection.id] ?? []).map((tool) => tool.name)
      const set =
        !entry || entry.toolNames === "all"
          ? new Set<string>(entry?.toolNames === "all" ? all : [])
          : new Set(entry.toolNames)

      if (set.has(toolName)) set.delete(toolName)
      else set.add(toolName)

      if (set.size === 0) delete next[connection.id]
      else next[connection.id] = { connectionId: connection.id, toolNames: set }
      return next
    })
  }

  function buildToolPayload() {
    const payload: { connection_id: string; tool_name: string }[] = []
    for (const selection of Object.values(selections)) {
      if (selection.toolNames === "all") {
        payload.push({ connection_id: selection.connectionId, tool_name: "*" })
      } else {
        for (const toolName of selection.toolNames) {
          payload.push({ connection_id: selection.connectionId, tool_name: toolName })
        }
      }
    }
    return payload
  }

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setSaving(true)
    const payload = {
      name,
      description: description || null,
      provider_key: providerKey,
      model: model || null,
      system_prompt: systemPrompt || null,
      temperature: temperature === "" ? null : Number(temperature),
      max_iterations: Number(maxIterations),
      is_active: isActive,
      web_tools_enabled: webToolsEnabled,
      response_style: responseStyle,
      tools: buildToolPayload(),
    }
    try {
      const agent = agentId
        ? await api.updateAgent(agentId, payload)
        : await api.createAgent(payload)
      toast.success(t("editor.toasts.saved", { name: agent.name }))
      navigate("/agents")
    } catch (error) {
      toastApiError(error, t("editor.toasts.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-muted-foreground p-6 text-sm">{t("common.loading")}</div>
  }

  const settingsForm = (
    <form onSubmit={save} className="space-y-6">
        {providers.length === 0 && (
          <Alert variant="destructive">
            <AlertTitle>{t("editor.noProvider.title")}</AlertTitle>
            <AlertDescription>{t("editor.noProvider.description")}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t("editor.basics.title")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">{t("editor.basics.nameLabel")}</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("editor.basics.namePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t("editor.basics.descriptionLabel")}</Label>
              <Input
                id="description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t("editor.basics.descriptionPlaceholder")}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="prompt">{t("editor.basics.systemPromptLabel")}</Label>
              <Textarea
                id="prompt"
                rows={6}
                value={systemPrompt}
                onChange={(event) => setSystemPrompt(event.target.value)}
                placeholder={t("editor.basics.systemPromptPlaceholder")}
              />
              <p className="text-muted-foreground text-xs">{t("editor.basics.systemPromptHint")}</p>
            </div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="active">{t("editor.basics.activeLabel")}</Label>
            </div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Switch
                id="web-tools"
                checked={webToolsEnabled}
                onCheckedChange={setWebToolsEnabled}
              />
              <Label htmlFor="web-tools">{t("editor.basics.webToolsLabel")}</Label>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="response-style">{t("editor.basics.responseStyleLabel")}</Label>
              <Select
                value={responseStyle}
                onValueChange={(value) => setResponseStyle(value as ResponseStyle)}
              >
                <SelectTrigger id="response-style" className="md:w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="concise">
                    {t("editor.basics.responseStyleConcise")}
                  </SelectItem>
                  <SelectItem value="extended">
                    {t("editor.basics.responseStyleExtended")}
                  </SelectItem>
                  <SelectItem value="detailed">
                    {t("editor.basics.responseStyleDetailed")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {t("editor.basics.responseStyleHint")}
              </p>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>{t("editor.model.title")}</CardTitle>
            <CardDescription>{t("editor.model.description")}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="provider">{t("editor.model.providerLabel")}</Label>
              <Select
                value={providerKey}
                onValueChange={(value) => {
                  setProviderKey(value)
                  const next = providers.find((entry) => entry.provider_key === value)
                  setModel(next?.default_model ?? "")
                }}
              >
                <SelectTrigger id="provider">
                  <SelectValue placeholder={t("editor.model.providerPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((entry) => (
                    <SelectItem key={entry.provider_key} value={entry.provider_key}>
                      {entry.label}
                      {entry.ready ? "" : t("editor.model.noApiKeySuffix")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">{t("editor.model.modelLabel")}</Label>
              {provider && provider.models.length > 0 ? (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger id="model">
                    <SelectValue placeholder={t("editor.model.modelPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {provider.models.map((entry) => (
                      <SelectItem key={entry} value={entry}>
                        {entry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="model"
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                  placeholder="deepseek-flash"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="temperature">{t("editor.model.temperatureLabel")}</Label>
              <Input
                id="temperature"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(event) => setTemperature(event.target.value)}
                placeholder={t("editor.model.temperaturePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="iterations">{t("editor.model.stepLimitLabel")}</Label>
              <Input
                id="iterations"
                type="number"
                min={1}
                max={100}
                value={maxIterations}
                onChange={(event) => setMaxIterations(event.target.value)}
              />
              <p className="text-muted-foreground text-xs">{t("editor.model.stepLimitHint")}</p>
            </div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t("editor.toolsSection.title")}</CardTitle>
            <CardDescription>{t("editor.toolsSection.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {connections.length === 0 ? (
              <Alert>
                <AlertTitle>{t("editor.toolsSection.emptyTitle")}</AlertTitle>
                <AlertDescription>
                  {t("editor.toolsSection.emptyDescription")}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => navigate("/connections")}
                  >
                    {t("editor.toolsSection.goToServers")}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              connections.map((connection) => {
                const selection = selections[connection.id]
                const tools = (toolsByConnection[connection.id] ?? []).filter(
                  (tool) => !tool.unsupported_reason,
                )
                const allSelected = selection?.toolNames === "all"
                return (
                  <div key={connection.id} className="rounded-lg border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium">{connection.name}</div>
                        <div className="text-muted-foreground text-xs">
                          {t("editor.toolsSection.availableCount", { count: tools.length })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`all-${connection.id}`} className="text-xs font-normal">
                          {t("editor.toolsSection.useAllLabel")}
                        </Label>
                        <Switch
                          id={`all-${connection.id}`}
                          checked={allSelected}
                          onCheckedChange={(checked) => toggleConnection(connection, checked)}
                        />
                      </div>
                    </div>

                    {tools.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {tools.map((tool) => {
                          const picked =
                            allSelected ||
                            (selection?.toolNames !== "all" &&
                              selection?.toolNames.has(tool.name))
                          return (
                            <button
                              key={tool.id}
                              type="button"
                              onClick={() => toggleTool(connection, tool.name)}
                              title={tool.description ?? tool.name}
                            >
                              <Badge variant={picked ? "default" : "outline"}>{tool.name}</Badge>
                            </button>
                          )
                        })}
                      </div>
                    )}
                    {allSelected && (
                      <p className="text-muted-foreground mt-2 text-xs">
                        {t("editor.toolsSection.autoIncludeHint")}
                      </p>
                    )}
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>

        {isNew && (
          <Card>
            <CardHeader>
              <CardTitle>{t("editor.saveFirst.title")}</CardTitle>
              <CardDescription>{t("editor.saveFirst.description")}</CardDescription>
            </CardHeader>
          </Card>
        )}

        <div className="flex gap-2">
          <Button type="submit" disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {saving ? t("editor.submit.saving") : t("editor.submit.save")}
          </Button>
          {agentId && (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive"
              onClick={async () => {
                if (!confirm(t("editor.deleteConfirm", { name }))) return
                try {
                  await api.deleteAgent(agentId)
                  toast.success(t("editor.toasts.deleted"))
                  navigate("/agents")
                } catch (error) {
                  toast.error(
                    error instanceof ApiError ? error.message : t("editor.toasts.deleteFailed"),
                  )
                }
              }}
            >
              <Trash2 className="size-4" />
              {t("editor.deleteButton")}
            </Button>
          )}
        </div>
    </form>
  )

  return (
    <>
      <PageHeader
        title={isNew ? t("editor.title.new") : name || t("editor.title.edit")}
        description={t("editor.description")}
        action={
          <Button variant="outline" onClick={() => navigate("/agents")}>
            <ArrowLeft className="size-4" />
            {t("editor.back")}
          </Button>
        }
      />

      <div className="p-6 pb-24">
        {agentId ? (
          <Tabs defaultValue={initialTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="settings">{t("editor.tabs.settings")}</TabsTrigger>
              <TabsTrigger value="plugins">{t("editor.tabs.plugins")}</TabsTrigger>
              <TabsTrigger value="telegram">{t("editor.tabs.telegram")}</TabsTrigger>
              <TabsTrigger value="memory">{t("editor.tabs.memory")}</TabsTrigger>
            </TabsList>
            <TabsContent value="settings">{settingsForm}</TabsContent>
            <TabsContent value="plugins">
              <PluginsTab agentId={agentId} initialSlug={searchParams.get("plugin") ?? undefined} />
            </TabsContent>
            <TabsContent value="telegram">
              <TelegramCard agentId={agentId} />
            </TabsContent>
            <TabsContent value="memory">
              <MemoryTab agentId={agentId} />
            </TabsContent>
          </Tabs>
        ) : (
          settingsForm
        )}
      </div>
    </>
  )
}
